import { useMemo, useState, useCallback, useEffect } from 'react'
import dayjs from 'dayjs'
import isBetween from 'dayjs/plugin/isBetween'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Calendar } from '@/components/ui/calendar'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { CalendarIcon } from '@radix-ui/react-icons'
import { Info } from 'lucide-react'
import { cn } from '@/lib/utils'
import { formatUSD } from '@/lib/format-utils'
import { useToast } from '@/hooks/use-toast'
import RevenueMixChart from './revenue-mix-chart'
import {
  calculatePredictedRevenue,
  generateDailyBudgetsFromMonthly,
  getLastYearMonthlyCost,
  buildAvgRoasByYear,
  getCohortRange,
  pivotRevenueMixData,
} from '../utils'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { ScrollArea } from '@/components/ui/scroll-area'
import {
  Area,
  AreaChart,
  CartesianGrid,
  XAxis,
  YAxis,
  ReferenceLine,
  Line,
  ComposedChart,
} from 'recharts'
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from '@/components/ui/chart'

dayjs.extend(isBetween)

// 利润图表配置
const profitChartConfig = {
  profit: {
    label: 'Profit',
    color: '#22c55e',
  },
  lastYearProfit: {
    label: 'Last Year Profit',
    color: '#22c55e',
  },
  revenue: {
    label: 'Revenue',
    color: '#3b82f6',
  },
  lastYearRevenue: {
    label: 'Last Year Revenue',
    color: '#3b82f6',
  },
  cost: {
    label: 'Cost',
    color: '#ef4444',
  },
  lastYearCost: {
    label: 'Last Year Cost',
    color: '#ef4444',
  },
  targetProfit: {
    label: 'Target Profit',
    color: '#64748b',
  },
}

/**
 * 收入预测面板 (Budget Simulator)
 * 基于用户定义的预算和去年同期 ROAS 曲线预测未来收入
 */
const RevenuePredictionView = ({ roasData }) => {
  const { toast } = useToast()
  const [displayMode, setDisplayMode] = useState('value')
  const [trendMode, setTrendMode] = useState('daily')

  // 利润图表中显示的指标（默认只显示 profit）
  const [visibleMetrics, setVisibleMetrics] = useState({
    profit: true,
    revenue: false,
    cost: false,
  })

  // 切换指标显示状态
  const toggleMetric = useCallback((metric) => {
    setVisibleMetrics((prev) => ({
      ...prev,
      [metric]: !prev[metric],
    }))
  }, [])

  // 预测日期范围：默认为当年全年 (1月1日 - 12月31日)
  const currentYear = dayjs().year()
  const [predictionRange, setPredictionRange] = useState({
    from: dayjs(`${currentYear}-01-01`).toDate(),
    to: dayjs(`${currentYear}-12-31`).toDate(),
  })
  const [rangeOpen, setRangeOpen] = useState(false)

  // 手动输入的日期字符串
  const [startDateInput, setStartDateInput] = useState(`${currentYear}-01-01`)
  const [endDateInput, setEndDateInput] = useState(`${currentYear}-12-31`)

  // 全局日均预算
  const [globalDailyBudget, setGlobalDailyBudget] = useState(10000)
  const [globalDailyBudgetDraft, setGlobalDailyBudgetDraft] = useState('10000')
  const [hasEditedGlobalBudget, setHasEditedGlobalBudget] = useState(false)
  const [useLastYearDailyOverrides, setUseLastYearDailyOverrides] = useState(false)
  const [targetDailyProfit, setTargetDailyProfit] = useState(0)
  const [targetDailyProfitDraft, setTargetDailyProfitDraft] = useState('0')
  const [hasEditedTargetDailyProfit, setHasEditedTargetDailyProfit] = useState(false)

  // 月度预算目标（用户可编辑）
  const [monthlyBudgets, setMonthlyBudgets] = useState({})
  const [monthlyBudgetDrafts, setMonthlyBudgetDrafts] = useState({})

  // 是否已生成预算
  const [budgetGenerated, setBudgetGenerated] = useState(false)

  // 处理手动输入日期变更
  const handleStartDateInputChange = useCallback((value) => {
    setStartDateInput(value)
    const parsed = dayjs(value, 'YYYY-MM-DD')
    if (parsed.isValid()) {
      setPredictionRange((prev) => ({ ...prev, from: parsed.toDate() }))
      setBudgetGenerated(false)
    }
  }, [])

  const handleEndDateInputChange = useCallback((value) => {
    setEndDateInput(value)
    const parsed = dayjs(value, 'YYYY-MM-DD')
    if (parsed.isValid()) {
      setPredictionRange((prev) => ({ ...prev, to: parsed.toDate() }))
      setBudgetGenerated(false)
    }
  }, [])

  // 日历选择同步到输入框
  const handleCalendarSelect = useCallback((range) => {
    if (range) {
      setPredictionRange(range)
      if (range.from) {
        setStartDateInput(dayjs(range.from).format('YYYY-MM-DD'))
      }
      if (range.to) {
        setEndDateInput(dayjs(range.to).format('YYYY-MM-DD'))
      }
      setBudgetGenerated(false)
    }
  }, [])

  // 验证日期范围
  const validateDateRange = useCallback(() => {
    const start = dayjs(startDateInput, 'YYYY-MM-DD')
    const end = dayjs(endDateInput, 'YYYY-MM-DD')

    if (!start.isValid() || !end.isValid()) {
      toast({
        title: 'Invalid Date Format',
        description: 'Please enter dates in YYYY-MM-DD format.',
        variant: 'destructive',
      })
      return false
    }

    if (start.isAfter(end)) {
      toast({
        title: 'Invalid Date Range',
        description: 'Start date must be before end date.',
        variant: 'destructive',
      })
      return false
    }

    return true
  }, [startDateInput, endDateInput, toast])

  const lastYearAvgRoas = useMemo(() => {
    if (!roasData?.dailyData) return { roasByDay: {}, maxDay: -1 }
    return buildAvgRoasByYear(roasData.dailyData, currentYear - 1)
  }, [roasData?.dailyData, currentYear])

  // 获取去年同期月度消耗数据（参考用）
  const lastYearMonthlyCost = useMemo(() => {
    if (!roasData?.dailyData || !predictionRange.from || !predictionRange.to) {
      return {}
    }
    return getLastYearMonthlyCost(
      roasData.dailyData,
      dayjs(predictionRange.from).format('YYYY-MM-DD'),
      dayjs(predictionRange.to).format('YYYY-MM-DD'),
    )
  }, [roasData?.dailyData, predictionRange])

  const lastYearDailyCostMap = useMemo(() => {
    if (!roasData?.dailyData) return new Map()
    const map = new Map()
    roasData.dailyData.forEach((item) => {
      if (item.install_date) {
        map.set(item.install_date, Number(item.cost) || 0)
      }
    })
    return map
  }, [roasData?.dailyData])

  const actualDataEndDate = useMemo(() => {
    if (!roasData?.dailyData) return null
    const dates = roasData.dailyData
      .filter((item) => dayjs(item.install_date).year() === currentYear)
      .map((item) => dayjs(item.install_date))
    if (dates.length === 0) return null
    const maxDate = dates.reduce((max, d) => (d.isAfter(max) ? d : max), dates[0])
    return maxDate
  }, [roasData?.dailyData, currentYear])

  const roundByMagnitude = useCallback((value) => {
    const safeValue = Math.max(0, value || 0)
    if (safeValue === 0) return 0
    const magnitude = Math.pow(10, Math.floor(Math.log10(safeValue)))
    return Math.ceil(safeValue / magnitude) * magnitude
  }, [])

  // 计算去年同期的全局日均消耗
  const lastYearGlobalDailyAvg = useMemo(() => {
    if (Object.keys(lastYearMonthlyCost).length === 0) return null

    let totalCost = 0
    let totalDays = 0
    Object.values(lastYearMonthlyCost).forEach((data) => {
      totalCost += data.total || 0
      totalDays += data.days || 0
    })

    return totalDays > 0 ? totalCost / totalDays : null
  }, [lastYearMonthlyCost])

  const roundedLastYearDaily = useMemo(() => {
    if (lastYearGlobalDailyAvg === null) return null
    return roundByMagnitude(lastYearGlobalDailyAvg)
  }, [lastYearGlobalDailyAvg, roundByMagnitude])


  useEffect(() => {
    if (roundedLastYearDaily === null || hasEditedGlobalBudget) return
    setGlobalDailyBudget(roundedLastYearDaily)
    setGlobalDailyBudgetDraft(String(roundedLastYearDaily))
  }, [roundedLastYearDaily, hasEditedGlobalBudget])


  // 获取预测范围内的所有月份
  const monthsInRange = useMemo(() => {
    if (!predictionRange.from || !predictionRange.to) return []

    const months = []
    let current = dayjs(predictionRange.from).startOf('month')
    const end = dayjs(predictionRange.to).endOf('month')

    while (current.isBefore(end) || current.isSame(end, 'month')) {
      months.push({
        key: current.format('YYYY-MM'),
        label: current.format('YYYY年MM月'),
        daysInMonth: current.daysInMonth(),
        // 去年同期的 key
        lastYearKey: current.subtract(1, 'year').format('YYYY-MM'),
      })
      current = current.add(1, 'month')
    }

    return months
  }, [predictionRange])

  // 生成每日预算 Map
  const dailyBudgets = useMemo(() => {
    if (!predictionRange.from || !predictionRange.to || !budgetGenerated) {
      return {}
    }
    const baseBudgets = generateDailyBudgetsFromMonthly(
      monthlyBudgets,
      dayjs(predictionRange.from).format('YYYY-MM-DD'),
      dayjs(predictionRange.to).format('YYYY-MM-DD'),
      globalDailyBudget,
    )
    if (!useLastYearDailyOverrides) return baseBudgets

    const merged = { ...baseBudgets }
    let current = dayjs(predictionRange.from)
    const end = dayjs(predictionRange.to)
    while (current.isBefore(end) || current.isSame(end, 'day')) {
      const dateStr = current.format('YYYY-MM-DD')
      const lastYearDate = current.subtract(1, 'year').format('YYYY-MM-DD')
      if (lastYearDailyCostMap.has(lastYearDate)) {
        merged[dateStr] = lastYearDailyCostMap.get(lastYearDate)
      }
      current = current.add(1, 'day')
    }
    return merged
  }, [
    predictionRange,
    monthlyBudgets,
    globalDailyBudget,
    budgetGenerated,
    useLastYearDailyOverrides,
    lastYearDailyCostMap,
  ])

  // 计算预测收入
  const predictedDailyData = useMemo(() => {
    if (
      !roasData?.dailyData ||
      !predictionRange.from ||
      !predictionRange.to ||
      !budgetGenerated ||
      Object.keys(dailyBudgets).length === 0
    ) {
      return []
    }

    return calculatePredictedRevenue(
      dayjs(predictionRange.from).format('YYYY-MM-DD'),
      dayjs(predictionRange.to).format('YYYY-MM-DD'),
      dailyBudgets,
      roasData.dailyData,
    )
  }, [roasData?.dailyData, predictionRange, dailyBudgets, budgetGenerated])

  // 转换为图表数据（预测数据）
  const predictedChartData = useMemo(() => {
    if (predictedDailyData.length === 0) return []
    return pivotRevenueMixData(predictedDailyData, true, 'daily')
  }, [predictedDailyData])

  const revenueDateSourceMap = useMemo(() => {
    if (predictedDailyData.length === 0) return new Map()
    const map = new Map()
    const start = predictionRange?.from ? dayjs(predictionRange.from) : null
    const end = predictionRange?.to ? dayjs(predictionRange.to) : null
    const cutoff = actualDataEndDate ? dayjs(actualDataEndDate) : null

    predictedDailyData.forEach((item) => {
      const sourceType = item.sourceType || 'exact'
      const installDate = dayjs(item.install_date)
      Object.keys(item.days || {}).forEach((dayKey) => {
        const dayN = parseInt(dayKey.replace('day', ''), 10)
        if (isNaN(dayN)) return
        const revenueDate = installDate.add(dayN, 'day')
        if (start && end) {
          if (revenueDate.isBefore(start) || revenueDate.isAfter(end)) return
        }
        const key = revenueDate.format('YYYY-MM-DD')
        if (!map.has(key)) map.set(key, new Set())
        map.get(key).add(sourceType)
      })
    })

    const labelMap = new Map()
    map.forEach((set, date) => {
      if (set.has('yearly')) {
        labelMap.set(date, { sourceType: 'yearly', sourceLabel: '全年平均' })
      } else if (set.has('monthly')) {
        labelMap.set(date, { sourceType: 'monthly', sourceLabel: '同月平均' })
      } else {
        labelMap.set(date, { sourceType: 'exact', sourceLabel: '同日' })
      }
    })
    return labelMap
  }, [predictedDailyData, predictionRange])

  // 去年同期的收入构成数据（用于图表对比）
  const lastYearChartData = useMemo(() => {
    if (!roasData?.dailyData) return []
    return pivotRevenueMixData(roasData.dailyData, true, 'daily')
  }, [roasData?.dailyData])

  // 去年同期的每日利润数据（用于对比）
  const lastYearProfitMap = useMemo(() => {
    if (!roasData?.dailyData) return new Map()

    // 先用 pivotRevenueMixData 转换去年数据为按收入日期组织
    const lastYearMixData = pivotRevenueMixData(roasData.dailyData, true, 'daily')

    // 构建去年每日利润 Map
    const map = new Map()
    const costByDate = new Map()

    // 构建去年每日成本 Map
    roasData.dailyData.forEach((item) => {
      if (item.install_date && item.cost) {
        costByDate.set(item.install_date, Number(item.cost) || 0)
      }
    })

    // 计算去年每日利润
    lastYearMixData.forEach((item) => {
      const revenue = item.total || 0
      const cost = costByDate.get(item.revenue_date) || 0
      const profit = revenue - cost
      map.set(item.revenue_date, { revenue, cost, profit })
    })

    return map
  }, [roasData?.dailyData])

  const lastYearGlobalDailyProfitAvg = useMemo(() => {
    if (!predictionRange.from || !predictionRange.to) return null
    const start = dayjs(predictionRange.from).subtract(1, 'year')
    const end = dayjs(predictionRange.to).subtract(1, 'year')
    let totalProfit = 0
    let totalDays = 0
    lastYearProfitMap.forEach((value, date) => {
      const d = dayjs(date)
      if (d.isBetween(start, end, 'day', '[]')) {
        totalProfit += value.profit || 0
        totalDays += 1
      }
    })
    return totalDays > 0 ? totalProfit / totalDays : null
  }, [predictionRange, lastYearProfitMap])

  const roundedLastYearDailyProfit = useMemo(() => {
    if (lastYearGlobalDailyProfitAvg === null) return null
    return roundByMagnitude(lastYearGlobalDailyProfitAvg)
  }, [lastYearGlobalDailyProfitAvg, roundByMagnitude])

  useEffect(() => {
    if (roundedLastYearDailyProfit === null || hasEditedTargetDailyProfit) return
    setTargetDailyProfit(roundedLastYearDailyProfit)
    setTargetDailyProfitDraft(String(roundedLastYearDailyProfit))
  }, [roundedLastYearDailyProfit, hasEditedTargetDailyProfit])

  // 真实数据 Map（用于覆盖预测数据）
  // 说明：收入按 revenue_date 汇总，允许包含上一年投放带来的收入
  const currentYearActualMap = useMemo(() => {
    if (!roasData?.dailyData) return new Map()

    const start = predictionRange?.from ? dayjs(predictionRange.from) : null
    const end = predictionRange?.to ? dayjs(predictionRange.to) : null
    const cutoff = actualDataEndDate ? dayjs(actualDataEndDate) : null

    // 用 pivotRevenueMixData 转换为按收入日期组织（获取每日收入）
    const mixData = pivotRevenueMixData(roasData.dailyData, true, 'daily')

    const getMaxDayFromDays = (days) => {
      if (!days) return -1
      const keys = Object.keys(days)
      if (keys.length === 0) return -1
      const maxDay = Math.max(
        ...keys
          .map((k) => parseInt(k.replace('day', ''), 10))
          .filter((n) => !isNaN(n)),
      )
      return isNaN(maxDay) ? -1 : maxDay
    }

    const { roasByDay, maxDay: maxAvgDay } = lastYearAvgRoas

    // 构建每日成本 Map（以 install_date 为 key）
    const costByDate = new Map()
    roasData.dailyData.forEach((item) => {
      if (!item.install_date) return
      const d = dayjs(item.install_date)
      if (d.year() !== currentYear) return
      if (cutoff && d.isAfter(cutoff)) return
      costByDate.set(item.install_date, Number(item.cost) || 0)
    })

    // 构建每日收入 Map（以 revenue_date 为 key）
    const revenueByDate = new Map()
    mixData.forEach((item) => {
      const date = dayjs(item.revenue_date)
      if (start && end) {
        if (date.isBefore(start) || date.isAfter(end)) return
      }
      if (cutoff && date.isAfter(cutoff)) return
      revenueByDate.set(item.revenue_date, {
        revenue: item.total || 0,
        ranges: item.ranges,
      })
    })

    // 用去年全年平均曲线补全上一年末投放的尾部收入
    const estimatedTailDates = new Set()
    if (maxAvgDay >= 0) {
      roasData.dailyData.forEach((item) => {
        if (!item.install_date) return
        const installDate = dayjs(item.install_date)
        if (installDate.year() !== currentYear - 1) return
        const cost = Number(item.cost) || 0
        if (cost <= 0) return
        const maxDayItem = getMaxDayFromDays(item.days)
        if (maxDayItem < 0 || maxDayItem >= maxAvgDay) return

        for (let k = maxDayItem + 1; k <= maxAvgDay; k++) {
          const dayKey = `day${k}`
          const roas = roasByDay[dayKey]
          if (roas === undefined) continue
          const revenue = cost * roas
          if (revenue === 0) continue
          const revenueDate = installDate.add(k, 'day')
          if (start && end) {
            if (revenueDate.isBefore(start) || revenueDate.isAfter(end)) continue
          }
          if (cutoff && revenueDate.isAfter(cutoff)) continue
          const dateStr = revenueDate.format('YYYY-MM-DD')
          const existing = revenueByDate.get(dateStr) || { revenue: 0, ranges: {} }
          existing.revenue += revenue
          const range = getCohortRange(k, true)
          existing.ranges[range] = (existing.ranges[range] || 0) + revenue
          revenueByDate.set(dateStr, existing)
          estimatedTailDates.add(dateStr)
        }
      })
    }

    // 合并：以所有有真实数据的日期为基准
    // 对于每个日期，成本来自当天的 install_date 数据，收入来自 pivotRevenueMixData 转换后的数据
    const map = new Map()
    const allDates = new Set([...costByDate.keys(), ...revenueByDate.keys()])

    allDates.forEach((date) => {
      if (start && end) {
        const d = dayjs(date)
        if (d.isBefore(start) || d.isAfter(end)) return
      }
      if (cutoff) {
        const d = dayjs(date)
        if (d.isAfter(cutoff)) return
      }
      const cost = costByDate.get(date) || 0
      const revenueData = revenueByDate.get(date)
      const revenue = revenueData?.revenue || 0
      const profit = revenue - cost
      map.set(date, {
        revenue,
        cost,
        profit,
        ranges: revenueData?.ranges,
        hasCost: costByDate.has(date),
        hasRevenue: revenueByDate.has(date),
        isEstimated: estimatedTailDates.has(date),
      })
    })

    return map
  }, [
    roasData?.dailyData,
    predictionRange,
    currentYear,
    lastYearAvgRoas,
    actualDataEndDate,
  ])

  // 合并预测数据和真实数据（真实数据覆盖预测数据）
  const chartData = useMemo(() => {
    const map = new Map()
    const daysOfWeek = ['日', '一', '二', '三', '四', '五', '六']

    // 先放入预测数据
    predictedChartData.forEach((item) => {
      map.set(item.revenue_date, { ...item, isActual: false })
    })

    // 再用真实数据覆盖或补全缺失日期
    currentYearActualMap.forEach((actualData, date) => {
      if (predictionRange?.from && predictionRange?.to) {
        const inRange = dayjs(date).isBetween(
          predictionRange.from,
          predictionRange.to,
          'day',
          '[]',
        )
        if (!inRange) return
      }

      const existing = map.get(date)
      const displayDate =
        existing?.display_date ||
        `${date} (周${daysOfWeek[dayjs(date).day()]})`

      map.set(date, {
        ...(existing || {
          revenue_date: date,
          display_date: displayDate,
          total: 0,
          cohorts: {},
          ranges: {},
        }),
        total: actualData.revenue,
        ranges: actualData.ranges || existing?.ranges || {},
        isActual: true,
        display_date: `${displayDate} ✓`,
      })
    })

    return Array.from(map.values()).sort((a, b) =>
      a.revenue_date.localeCompare(b.revenue_date),
    )
  }, [predictedChartData, currentYearActualMap, predictionRange])

  // 利润图表数据：按日期计算 revenue - cost，并包含去年同期数据
  // 对于有真实数据的日期，用真实数据覆盖预测数据
  const profitChartData = useMemo(() => {
    if (chartData.length === 0 || Object.keys(dailyBudgets).length === 0) return []

    // 将 chartData (按 revenue_date 组织) 与 dailyBudgets (按 spend_date 组织) 合并
    // 对于每个 revenue_date，需要计算当天的总成本贡献
    // 简化处理：使用 dailyBudgets 中对应日期的成本作为当天成本
    return chartData
      .map((item) => {
        // 检查是否有今年的真实数据
        const actualData = currentYearActualMap.get(item.revenue_date)
        const isActual = actualData !== undefined

        // 如果有真实数据，使用真实数据；否则使用预测数据
        const revenue = isActual ? actualData.revenue : (item.total || 0)
        const cost = isActual ? actualData.cost : (dailyBudgets[item.revenue_date] || 0)
        const profit = revenue - cost

        const sourceInfo = revenueDateSourceMap.get(item.revenue_date)
        const sourceLabel = isActual
          ? actualData?.isEstimated
            ? '真实+补全'
            : '真实数据'
          : (sourceInfo?.sourceLabel || '同日')
        const sourceType = isActual
          ? actualData?.isEstimated
            ? 'actualEstimated'
            : 'actual'
          : (sourceInfo?.sourceType || 'exact')

        // 获取去年同期的数据（revenue, cost, profit）
        const lastYearDate = dayjs(item.revenue_date).subtract(1, 'year').format('YYYY-MM-DD')
        const lastYearData = lastYearProfitMap.get(lastYearDate)
        const lastYearProfit = lastYearData?.profit ?? null
        const lastYearRevenue = lastYearData?.revenue ?? null
        const lastYearCost = lastYearData?.cost ?? null

        return {
          date: item.revenue_date,
          displayDate: item.display_date + (isActual ? ' ✓' : ''),
          revenue,
          cost,
          profit,
          lastYearRevenue,
          lastYearCost,
          lastYearProfit,
          lastYearDate,
          isActual, // 标记是否为真实数据
          sourceLabel,
          sourceType,
        }
      })
      .sort((a, b) => a.date.localeCompare(b.date))
  }, [
    chartData,
    dailyBudgets,
    lastYearProfitMap,
    currentYearActualMap,
    revenueDateSourceMap,
  ])

  const trendChartData = useMemo(() => {
    if (profitChartData.length === 0) return []
    if (trendMode === 'daily') return profitChartData

    const grouped = new Map()
    profitChartData.forEach((item) => {
      let key = item.date
      let displayDate = item.displayDate
      if (trendMode === 'weekly') {
        const startOfWeek = dayjs(item.date).startOf('week')
        const endOfWeek = startOfWeek.add(6, 'day')
        key = startOfWeek.format('YYYY-MM-DD')
        displayDate = `${startOfWeek.format('YYYY-MM-DD')} - ${endOfWeek.format('YYYY-MM-DD')}`
      } else if (trendMode === 'monthly') {
        const startOfMonth = dayjs(item.date).startOf('month')
        key = startOfMonth.format('YYYY-MM-DD')
        displayDate = startOfMonth.format('YYYY-MM')
      }

      if (!grouped.has(key)) {
        grouped.set(key, {
          date: key,
          displayDate,
          revenue: 0,
          cost: 0,
          profit: 0,
          lastYearRevenue: 0,
          lastYearCost: 0,
          lastYearProfit: 0,
          lastYearDate: dayjs(key).subtract(1, 'year').format('YYYY-MM-DD'),
          isActual: false,
          daysCount: 0,
          targetProfit: 0,
          sourceTypes: new Set(),
        })
      }

      const group = grouped.get(key)
      group.revenue += item.revenue || 0
      group.cost += item.cost || 0
      group.profit += item.profit || 0
      group.lastYearRevenue += item.lastYearRevenue || 0
      group.lastYearCost += item.lastYearCost || 0
      group.lastYearProfit += item.lastYearProfit || 0
      group.isActual = group.isActual || item.isActual
      group.daysCount += 1
      group.sourceTypes.add(item.sourceType)
    })

    return Array.from(grouped.values())
      .map((group) => {
        let sourceLabel = '同日'
        if (group.sourceTypes.has('yearly')) {
          sourceLabel = '包含全年平均'
        } else if (group.sourceTypes.has('monthly')) {
          sourceLabel = '包含同月平均'
        } else if (group.sourceTypes.has('actualEstimated') && group.sourceTypes.size === 1) {
          sourceLabel = '真实+补全'
        } else if (group.sourceTypes.has('actual') && group.sourceTypes.size === 1) {
          sourceLabel = '真实数据'
        } else if (group.sourceTypes.has('actual') || group.sourceTypes.has('actualEstimated')) {
          sourceLabel = '混合'
        }

        return {
          ...group,
          targetProfit: targetDailyProfit * (group.daysCount || 1),
          sourceLabel,
        }
      })
      .sort((a, b) => a.date.localeCompare(b.date))
  }, [profitChartData, trendMode, targetDailyProfit])

  // 汇总统计（使用 profitChartData，包含真实数据覆盖）
  const summary = useMemo(() => {
    if (profitChartData.length === 0) return null

    // 使用 profitChartData 计算，因为它已经合并了真实数据
    const totalRevenue = profitChartData.reduce((sum, item) => sum + item.revenue, 0)
    const totalCost = profitChartData.reduce((sum, item) => sum + item.cost, 0)
    const totalProfit = profitChartData.reduce((sum, item) => sum + item.profit, 0)
    const avgDailyRevenue = profitChartData.length > 0 ? totalRevenue / profitChartData.length : 0
    const avgDailyProfit = profitChartData.length > 0 ? totalProfit / profitChartData.length : 0
    const overallRoas = totalCost > 0 ? (totalRevenue / totalCost) * 100 : 0

    // 统计真实数据天数
    const actualDays = profitChartData.filter((item) => item.isActual).length

    return {
      totalRevenue,
      totalCost,
      totalProfit,
      avgDailyRevenue,
      avgDailyProfit,
      overallRoas,
      days: profitChartData.length,
      actualDays, // 真实数据天数
    }
  }, [profitChartData])

  // 月度预算输入：先写入草稿，避免频繁触发重算
  const handleMonthlyBudgetDraftChange = useCallback((monthKey, value) => {
    setMonthlyBudgetDrafts((prev) => ({
      ...prev,
      [monthKey]: value,
    }))
    setUseLastYearDailyOverrides(false)
  }, [])

  const commitMonthlyBudgetDraft = useCallback((monthKey) => {
    setMonthlyBudgetDrafts((prevDrafts) => {
      const draftValue = prevDrafts[monthKey]
      setMonthlyBudgets((prevBudgets) => {
        const next = { ...prevBudgets }
        if (draftValue === '' || draftValue === undefined) {
          delete next[monthKey]
        } else {
          const numValue = parseFloat(draftValue)
          next[monthKey] = isNaN(numValue) ? 0 : numValue
        }
        return next
      })
      return prevDrafts
    })
  }, [])

  // 应用全局预算
  const handleApplyGlobalBudget = useCallback(() => {
    if (!validateDateRange()) return
    // 重置月度预算为空，让 generateDailyBudgetsFromMonthly 使用全局值
    setMonthlyBudgets({})
    setMonthlyBudgetDrafts({})
    setUseLastYearDailyOverrides(false)
    setBudgetGenerated(true)
  }, [validateDateRange])

  const handleGlobalDailyBudgetDraftChange = useCallback((value) => {
    setGlobalDailyBudgetDraft(value)
    setHasEditedGlobalBudget(true)
    setUseLastYearDailyOverrides(false)
  }, [])

  const commitGlobalDailyBudgetDraft = useCallback(() => {
    const numValue = parseFloat(globalDailyBudgetDraft)
    const nextValue = isNaN(numValue) ? 0 : numValue
    setGlobalDailyBudget(nextValue)
    setGlobalDailyBudgetDraft(String(nextValue))
  }, [globalDailyBudgetDraft])

  const handleTargetDailyProfitDraftChange = useCallback((value) => {
    setTargetDailyProfitDraft(value)
    setHasEditedTargetDailyProfit(true)
  }, [])

  const commitTargetDailyProfitDraft = useCallback(() => {
    const numValue = parseFloat(targetDailyProfitDraft)
    const nextValue = isNaN(numValue) ? 0 : numValue
    setTargetDailyProfit(nextValue)
    setTargetDailyProfitDraft(String(nextValue))
  }, [targetDailyProfitDraft])

  // 使用去年同期数据填充月度预算（存储日均值）
  const handleUseLastYearBudget = useCallback(() => {
    if (!validateDateRange()) return
    const hasAnyLastYear = monthsInRange.some((month) => {
      const lastYearData = lastYearMonthlyCost[month.lastYearKey]
      return lastYearData?.daily
    })
    if (!hasAnyLastYear) {
      toast({
        title: 'No last year data',
        description: 'No last year daily costs are available for this range.',
        variant: 'destructive',
      })
      return
    }
    const newMonthlyBudgets = {}
    monthsInRange.forEach((month) => {
      const lastYearData = lastYearMonthlyCost[month.lastYearKey]
      if (lastYearData?.daily) {
        // 存储日均值
        newMonthlyBudgets[month.key] = lastYearData.daily
      }
    })
    setMonthlyBudgets(newMonthlyBudgets)
    const draftValues = {}
    Object.entries(newMonthlyBudgets).forEach(([key, value]) => {
      draftValues[key] = String(value)
    })
    setMonthlyBudgetDrafts(draftValues)
    setUseLastYearDailyOverrides(true)
    setBudgetGenerated(true)
  }, [validateDateRange, monthsInRange, lastYearMonthlyCost, toast])

  return (
    <div className="space-y-4">
      {/* 顶部控制栏 */}
      <Card className="p-4">
        <div className="flex flex-wrap items-end gap-4">
          {/* 预测日期范围 - 支持手动输入 */}
          <div className="flex flex-col gap-1.5">
            <Label className="text-xs text-muted-foreground">Prediction Range</Label>
            <Popover open={rangeOpen} onOpenChange={setRangeOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    'w-[280px] justify-start text-left font-normal',
                    !predictionRange.from && 'text-muted-foreground',
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {startDateInput} - {endDateInput}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  initialFocus
                  mode="range"
                  defaultMonth={predictionRange.from}
                  selected={predictionRange}
                  onSelect={handleCalendarSelect}
                  numberOfMonths={2}
                />
                {/* 手动输入日期 */}
                <div className="flex flex-col gap-3 p-3 border-t bg-muted/10">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="flex flex-col gap-1">
                      <Label className="text-xs text-muted-foreground">Start Date</Label>
                      <Input
                        value={startDateInput}
                        onChange={(e) => handleStartDateInputChange(e.target.value)}
                        placeholder="YYYY-MM-DD"
                        className="h-8 text-xs bg-background"
                      />
                    </div>
                    <div className="flex flex-col gap-1">
                      <Label className="text-xs text-muted-foreground">End Date</Label>
                      <Input
                        value={endDateInput}
                        onChange={(e) => handleEndDateInputChange(e.target.value)}
                        placeholder="YYYY-MM-DD"
                        className="h-8 text-xs bg-background"
                      />
                    </div>
                  </div>
                  <div className="flex justify-end">
                    <Button size="sm" onClick={() => setRangeOpen(false)} className="h-8 px-4 text-xs">
                      Confirm
                    </Button>
                  </div>
                </div>
              </PopoverContent>
            </Popover>
          </div>

          {/* 全局日均预算 */}
          <div className="flex flex-col gap-1.5">
            <Label className="text-xs text-muted-foreground">Global Daily Budget ($)</Label>
            <div className="flex items-center gap-2">
              <Input
                type="number"
                value={globalDailyBudgetDraft}
                onChange={(e) => handleGlobalDailyBudgetDraftChange(e.target.value)}
                onBlur={commitGlobalDailyBudgetDraft}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.currentTarget.blur()
                  }
                }}
                className="w-[120px]"
                min={0}
              />
              {lastYearGlobalDailyAvg !== null && (
                <div className="flex items-center gap-1.5">
                  <span className="text-xs text-muted-foreground whitespace-nowrap">
                    Last Year: {formatUSD(lastYearGlobalDailyAvg)}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* 单日利润目标 */}
          <div className="flex flex-col gap-1.5">
            <Label className="text-xs text-muted-foreground">单日利润目标 ($)</Label>
            <div className="flex items-center gap-2">
              <Input
                type="number"
                value={targetDailyProfitDraft}
                onChange={(e) => handleTargetDailyProfitDraftChange(e.target.value)}
                onBlur={commitTargetDailyProfitDraft}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.currentTarget.blur()
                  }
                }}
                className="w-[120px]"
                min={0}
              />
              {summary && (
                <div className="flex items-center gap-2 text-xs text-muted-foreground whitespace-nowrap">
                  <span>预测日均利润: {formatUSD(summary.avgDailyProfit)}</span>
                  <span
                    className={cn(
                      'font-medium',
                      summary.avgDailyProfit - targetDailyProfit >= 0
                        ? 'text-green-600'
                        : 'text-red-600',
                    )}
                  >
                    差值: {formatUSD(summary.avgDailyProfit - targetDailyProfit)}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* 操作按钮 */}
          <div className="flex gap-2 items-center">
            <Button onClick={handleApplyGlobalBudget} variant="default">
              Apply Global
            </Button>
            <Button onClick={handleUseLastYearBudget} variant="outline">
              Use Last Year
            </Button>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-9 w-9"
                  title="说明"
                >
                  <Info className="h-4 w-4 text-muted-foreground" />
                </Button>
              </PopoverTrigger>
              <PopoverContent align="start" className="w-80">
                <div className="text-sm font-medium text-foreground mb-1">
                  预算应用说明
                </div>
                <div className="text-xs text-muted-foreground space-y-1">
                  <div>Use Last Year：使用去年同月的日均消耗填充每个月。</div>
                  <div>Apply Global：清空月度覆盖，所有日期使用全局日均预算。</div>
                </div>
                <div className="text-sm font-medium text-foreground mt-3 mb-1">
                  预测算法说明
                </div>
                <div className="text-xs text-muted-foreground space-y-1">
                  <div>以投放日为起点，使用去年同日的 ROAS 衰减曲线（D0-D90）预测收入。</div>
                  <div>若同日曲线无效，先寻找前后 ±3 天的有效曲线；仍缺失则使用全年平均曲线兜底。</div>
                  <div>某一天的收入由该日及之前所有投放日的贡献累加得到。</div>
                  <div>有真实数据的日期会用真实 revenue/cost/profit 覆盖预测值。</div>
                </div>
              </PopoverContent>
            </Popover>
          </div>

          <div className="ml-auto" />
        </div>
      </Card>

      {/* 主内容区域：左侧预算规划器，右侧结果 */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        {/* 左侧：月度预算规划器 */}
        <Card className="p-4 lg:col-span-1">
          <h3 className="text-sm font-medium mb-3">Monthly Budget Planner</h3>
          <ScrollArea className="h-[400px] pr-2">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs">Month</TableHead>
                  <TableHead className="text-xs text-right">Last Year Daily</TableHead>
                  <TableHead className="text-xs text-right">Daily Budget</TableHead>
                  <TableHead className="text-xs text-right">Monthly Total</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {monthsInRange.map((month) => {
                  const lastYearData = lastYearMonthlyCost[month.lastYearKey]
                  const currentDailyBudget = monthlyBudgets[month.key]
                  const draftValue = monthlyBudgetDrafts[month.key]
                  const draftNumber =
                    draftValue === '' || draftValue === undefined ? undefined : parseFloat(draftValue)
                  // 当前日均：优先草稿值，其次已提交值，否则使用全局日均
                  const effectiveDailyBudget =
                    draftNumber !== undefined && !isNaN(draftNumber)
                      ? draftNumber
                      : currentDailyBudget !== undefined
                        ? currentDailyBudget
                        : globalDailyBudget
                  // 月总额 = 日均 × 该月天数
                  const monthlyTotal = effectiveDailyBudget * month.daysInMonth

                  return (
                    <TableRow key={month.key}>
                      <TableCell className="text-xs py-2">{month.label}</TableCell>
                      <TableCell className="text-xs text-right text-muted-foreground py-2">
                        {lastYearData ? formatUSD(lastYearData.daily) : '-'}
                      </TableCell>
                      <TableCell className="py-1">
                        <Input
                          type="number"
                          value={draftValue ?? currentDailyBudget ?? ''}
                          onChange={(e) =>
                            handleMonthlyBudgetDraftChange(month.key, e.target.value)
                          }
                          onBlur={() => commitMonthlyBudgetDraft(month.key)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.currentTarget.blur()
                            }
                          }}
                          placeholder={globalDailyBudget.toFixed(0)}
                          className="w-[80px] h-7 text-xs text-right"
                          min={0}
                        />
                      </TableCell>
                      <TableCell className="text-xs text-right py-2 text-muted-foreground">
                        {formatUSD(monthlyTotal)}
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </ScrollArea>

          {/* 汇总信息 */}
          {summary && (
            <div className="mt-4 pt-4 border-t space-y-2">
              <div className="flex justify-between text-xs">
                <span className="text-muted-foreground">Total Cost:</span>
                <span className="font-medium">{formatUSD(summary.totalCost)}</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-muted-foreground">Total Revenue:</span>
                <span className="font-medium">{formatUSD(summary.totalRevenue)}</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-muted-foreground">Total Profit:</span>
                <span
                  className={cn(
                    'font-medium',
                    summary.totalProfit >= 0 ? 'text-green-600' : 'text-red-600',
                  )}
                >
                  {formatUSD(summary.totalProfit)}
                </span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-muted-foreground">Avg Daily Rev:</span>
                <span className="font-medium">{formatUSD(summary.avgDailyRevenue)}</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-muted-foreground">Avg Daily Profit:</span>
                <span
                  className={cn(
                    'font-medium',
                    summary.avgDailyProfit >= 0 ? 'text-green-600' : 'text-red-600',
                  )}
                >
                  {formatUSD(summary.avgDailyProfit)}
                </span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-muted-foreground">Overall ROAS:</span>
                <span className="font-medium">{summary.overallRoas.toFixed(2)}%</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-muted-foreground">Days:</span>
                <span className="font-medium">
                  {summary.days}
                  {summary.actualDays > 0 && (
                    <span className="text-green-600 ml-1">
                      ({summary.actualDays} actual)
                    </span>
                  )}
                </span>
              </div>
            </div>
          )}
        </Card>

        {/* 右侧：利润趋势图 */}
        <Card className="p-4 lg:col-span-3">
          <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
            <h3 className="text-sm font-medium">
              {trendMode === 'daily' ? 'Daily' : trendMode === 'weekly' ? 'Weekly' : 'Monthly'} Metrics Trend
            </h3>
            <div className="flex items-center gap-2 flex-wrap">
              <Tabs value={trendMode} onValueChange={setTrendMode}>
                <TabsList className="grid w-[210px] grid-cols-3">
                  <TabsTrigger value="daily">Daily</TabsTrigger>
                  <TabsTrigger value="weekly">Weekly</TabsTrigger>
                  <TabsTrigger value="monthly">Monthly</TabsTrigger>
                </TabsList>
              </Tabs>
            </div>
            {/* 指标切换按钮 */}
            <div className="flex items-center gap-2">
              {[
                { key: 'profit', label: 'Profit', color: '#22c55e' },
                { key: 'revenue', label: 'Revenue', color: '#3b82f6' },
                { key: 'cost', label: 'Cost', color: '#ef4444' },
              ].map((metric) => (
                <Button
                  key={metric.key}
                  variant={visibleMetrics[metric.key] ? 'default' : 'outline'}
                  size="sm"
                  className="h-7 px-2 text-xs gap-1.5"
                  onClick={() => toggleMetric(metric.key)}
                  style={{
                    backgroundColor: visibleMetrics[metric.key] ? metric.color : undefined,
                    borderColor: metric.color,
                    color: visibleMetrics[metric.key] ? 'white' : metric.color,
                  }}
                >
                  <div
                    className="w-2 h-2 rounded-full"
                    style={{ backgroundColor: visibleMetrics[metric.key] ? 'white' : metric.color }}
                  />
                  {metric.label}
                </Button>
              ))}
            </div>
          </div>
          {trendChartData.length > 0 ? (
            <ChartContainer config={profitChartConfig} className="h-[400px] w-full">
              <ComposedChart
                data={trendChartData}
                margin={{ left: 12, right: 12, top: 20, bottom: 20 }}
              >
                <CartesianGrid vertical={false} strokeDasharray="3 3" />
                <XAxis
                  dataKey="displayDate"
                  tickLine={false}
                  axisLine={false}
                  tickMargin={8}
                  minTickGap={50}
                  tick={{ fontSize: 10 }}
                />
                <YAxis
                  tickLine={false}
                  axisLine={false}
                  tickMargin={8}
                  tickFormatter={(val) => formatUSD(val)}
                  tick={{ fontSize: 10 }}
                />
                <ReferenceLine y={0} stroke="#888" strokeDasharray="3 3" />
                <Line
                  type="monotone"
                  dataKey="targetProfit"
                  stroke={profitChartConfig.targetProfit.color}
                  strokeDasharray="5 5"
                  dot={false}
                  isAnimationActive={false}
                />
                <ChartTooltip
                  cursor={false}
                  content={
                    <ChartTooltipContent
                      labelFormatter={(value, payload) => {
                        const lastYearDate = payload?.[0]?.payload?.lastYearDate
                        const sourceLabel = payload?.[0]?.payload?.sourceLabel
                        return `Date: ${value}${lastYearDate ? ` (Last Year: ${lastYearDate})` : ''}${sourceLabel ? ` | 来源: ${sourceLabel}` : ''}`
                      }}
                      formatter={(value, name, item) => {
                        // 根据 name 判断对应的维度是否显示
                        const metricMap = {
                          profit: 'profit',
                          lastYearProfit: 'profit',
                          revenue: 'revenue',
                          lastYearRevenue: 'revenue',
                          cost: 'cost',
                          lastYearCost: 'cost',
                        }
                        if (name === 'targetProfit') {
                          return (
                            <div className="flex items-center justify-between gap-4 w-full text-[10px]">
                              <div className="flex items-center gap-2">
                                <div
                                  className="w-2 h-2 flex-shrink-0 border-2"
                                  style={{
                                    borderColor: profitChartConfig.targetProfit.color,
                                    borderStyle: 'dashed',
                                  }}
                                />
                                <span className="whitespace-nowrap text-muted-foreground">
                                  {profitChartConfig.targetProfit.label}
                                </span>
                              </div>
                              <span className="font-medium">
                                {formatUSD(value)}
                              </span>
                            </div>
                          )
                        }
                        const parentMetric = metricMap[name]
                        if (!visibleMetrics[parentMetric]) return null

                        const color = profitChartConfig[name]?.color
                        const isLastYear = name.startsWith('lastYear')
                        return (
                          <div className="flex items-center justify-between gap-4 w-full text-[10px]">
                            <div className="flex items-center gap-2">
                              <div
                                className={cn('w-2 h-2 flex-shrink-0', isLastYear ? 'border-2' : 'rounded-full')}
                                style={{
                                  backgroundColor: isLastYear ? 'transparent' : color,
                                  borderColor: isLastYear ? color : undefined,
                                  borderStyle: isLastYear ? 'dashed' : undefined,
                                }}
                              />
                              <span className="whitespace-nowrap text-muted-foreground">
                                {profitChartConfig[name]?.label || name}
                              </span>
                            </div>
                            <span
                              className={cn(
                                'font-bold tabular-nums',
                                (name === 'profit' || name === 'lastYearProfit') &&
                                  value >= 0 &&
                                  'text-green-600',
                                (name === 'profit' || name === 'lastYearProfit') &&
                                  value < 0 &&
                                  'text-red-600',
                              )}
                            >
                              {value !== null ? formatUSD(value) : '-'}
                            </span>
                          </div>
                        )
                      }}
                    />
                  }
                />
                <defs>
                  <linearGradient id="fillProfit" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#22c55e" stopOpacity={0.8} />
                    <stop offset="95%" stopColor="#22c55e" stopOpacity={0.1} />
                  </linearGradient>
                </defs>
                {/* Cost - 红色实线 */}
                {visibleMetrics.cost && (
                  <Line
                    type="monotone"
                    dataKey="cost"
                    stroke="#ef4444"
                    strokeWidth={2}
                    dot={false}
                    isAnimationActive={false}
                  />
                )}
                {/* Last Year Cost - 红色虚线 */}
                {visibleMetrics.cost && (
                  <Line
                    type="monotone"
                    dataKey="lastYearCost"
                    stroke="#ef4444"
                    strokeWidth={2}
                    strokeDasharray="5 5"
                    dot={false}
                    isAnimationActive={false}
                    connectNulls={false}
                  />
                )}
                {/* Revenue - 蓝色实线 */}
                {visibleMetrics.revenue && (
                  <Line
                    type="monotone"
                    dataKey="revenue"
                    stroke="#3b82f6"
                    strokeWidth={2}
                    dot={false}
                    isAnimationActive={false}
                  />
                )}
                {/* Last Year Revenue - 蓝色虚线 */}
                {visibleMetrics.revenue && (
                  <Line
                    type="monotone"
                    dataKey="lastYearRevenue"
                    stroke="#3b82f6"
                    strokeWidth={2}
                    strokeDasharray="5 5"
                    dot={false}
                    isAnimationActive={false}
                    connectNulls={false}
                  />
                )}
                {/* Last Year Profit - 绿色虚线 */}
                {visibleMetrics.profit && (
                  <Line
                    type="monotone"
                    dataKey="lastYearProfit"
                    stroke="#22c55e"
                    strokeWidth={2}
                    strokeDasharray="5 5"
                    dot={false}
                    isAnimationActive={false}
                    connectNulls={false}
                  />
                )}
                {/* Profit - 绿色实线填充 */}
                {visibleMetrics.profit && (
                  <Area
                    type="monotone"
                    dataKey="profit"
                    stroke="#22c55e"
                    fill="url(#fillProfit)"
                    strokeWidth={2}
                    isAnimationActive={false}
                  />
                )}
              </ComposedChart>
            </ChartContainer>
          ) : (
            <div className="h-[400px] flex items-center justify-center text-muted-foreground">
              <p className="text-sm">
                Click "Apply Global" or "Use Last Year" to generate prediction
              </p>
            </div>
          )}
        </Card>
      </div>

      {/* 收入构成图 */}
      {chartData.length > 0 && (
        <Card className="p-4">
          <h3 className="text-sm font-medium mb-3">Predicted Revenue Composition</h3>
          <RevenueMixChart data={chartData} displayMode={displayMode} isSimplified={true} lastYearData={lastYearChartData} />
        </Card>
      )}
    </div>
  )
}

export default RevenuePredictionView
